import {
  isAuthRejected,
  isValidToken,
  refreshAccessToken,
  revokeRefreshToken,
  setSession,
  userClient,
} from "../utils/jwt";
import { gql } from "@apollo/client";
import { usePersistedReducer } from "hooks/usePersistedReducer";
import React, { createContext, useCallback, useEffect } from "react";

const INITIALIZE = "INITIALIZE";
const SIGN_IN = "SIGN_IN";
const SIGN_OUT = "SIGN_OUT";
const KEEP_SESSION = "KEEP_SESSION";

const ME_QUERY = gql`
  query MeQuery {
    userMe {
      id
      avatarLarge
      avatarMedium
      avatarSmall
      email
      firstName
      lastName
      verified
      isSuperuser
    }
  }
`;

const LOGIN_MUTATION = gql`
  mutation LoginMutation(
    $email: String!
    $password: String!
    $turnstileToken: String
  ) {
    tokenAuth(
      email: $email
      password: $password
      turnstileToken: $turnstileToken
    ) {
      success
      errors
      token {
        token
      }
      user {
        avatarLarge
        avatarMedium
        avatarSmall
        id
        email
        firstName
        lastName
        isSuperuser
        verified
      }
    }
  }
`;

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isSuperuser: false,
  user: null,
};

const JWTReducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        isSuperuser: action.payload.isSuperuser,
        user: action.payload.user,
      };
    case "SIGN_IN":
      return {
        ...state,
        isAuthenticated: true,
        isSuperuser: action.payload.isSuperuser,
        user: action.payload.user,
      };
    case "SIGN_OUT":
      return {
        ...state,
        isAuthenticated: false,
        isSuperuser: false,
        user: null,
      };
    case KEEP_SESSION:
      // Start-up could not reach the API. Keep whatever the last visit left,
      // persisted under "auth", so the app renders and its next request tries
      // again; on a first visit there is nothing to keep and the user simply
      // isn't signed in. Marking start-up done matters either way: pages that
      // wait on isInitialized would otherwise wait forever.
      return { ...state, isInitialized: true };

    default:
      return state;
  }
};

const AuthContext = createContext<any>(null);

function AuthProvider({ children }) {
  const { state, dispatch } = usePersistedReducer(
    JWTReducer,
    initialState,
    "auth"
  );

  const reloadUser = useCallback(
    async (shouldDispatch: () => boolean = () => true) => {
      const data = await userClient.query({
        query: ME_QUERY,
        fetchPolicy: "network-only",
      });

      const user = data.data.userMe;
      if (shouldDispatch()) {
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: user != null,
            isSuperuser: user?.isSuperuser ?? false,
            user,
          },
        });
      }

      return user;
    },
    [dispatch]
  );

  useEffect(() => {
    let isMounted = true;

    const signOut = () => {
      setSession(null);
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: false,
          isSuperuser: false,
          user: null,
        },
      });
    };

    const initialize = async () => {
      try {
        let accessToken = window.localStorage.getItem("accessToken");

        if (!accessToken || !isValidToken(accessToken)) {
          accessToken = await refreshAccessToken({ redirectOnFailure: false });
        }

        if (!isMounted) {
          return;
        }

        // refreshAccessToken resolves only with a token; anything else means
        // there is no session to load.
        if (!accessToken) {
          signOut();
          return;
        }

        setSession(accessToken);
        await reloadUser(() => isMounted);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        // Only the API's rejection of the refresh token ends the session, as
        // for requests made while the app runs (utils/jwt). A page load is
        // when a refresh is most often due, and also when the network is
        // least likely to be ready: a phone reopening a discarded tab, or a
        // reload during a deploy. Signing out then lost a valid session.
        // A rejection inside the user lookup arrives wrapped by Apollo.
        const wrapped = err as { networkError?: unknown };
        if (isAuthRejected(err) || isAuthRejected(wrapped?.networkError)) {
          signOut();
          return;
        }
        console.error(err);
        dispatch({ type: KEEP_SESSION });
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [reloadUser]);

  const signIn = async (email, password, turnstileToken = null) => {
    const data = await userClient.mutate({
      mutation: LOGIN_MUTATION,
      variables: {
        email: email,
        password: password,
        turnstileToken: turnstileToken,
      },
    });

    const tokenAuth = data.data.tokenAuth;
    if (!tokenAuth.success) {
      const errors = tokenAuth.errors;
      throw new Error(
        errors?.nonFieldErrors?.[0]?.message ||
          errors?.turnstileToken?.[0]?.message ||
          "Unable to sign in"
      );
    }
    setSession(tokenAuth.token.token);
    const user = tokenAuth.user;

    dispatch({
      type: SIGN_IN,
      payload: {
        isSuperuser: user.isSuperuser,
        user,
      },
    });
  };

  const signOut = async () => {
    await revokeRefreshToken();
    dispatch({ type: SIGN_OUT });
  };

  const resetPassword = (email) => console.log(email);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        signIn,
        signOut,
        resetPassword,
        reloadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
